import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import {
  AdminActivityLog,
  AdminActivityLogDto,
  adminActivityFieldLabel,
  adminActivityValueLabel,
} from '../../../../core/models/system-settings/admin-activity-log.model';
import { MapperAdminActivityLogService } from '../../../../core/mappers/system-settings/mapper-admin-activity-log.service';
import { ApiAdminActivityLogService } from '../../../../core/services/api/system-settings/api-admin-activity-log.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-admin-activity-log',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './admin-activity-log.component.html',
  styleUrl: './admin-activity-log.component.css',
})
export class AdminActivityLogComponent implements OnInit {
  private readonly api = inject(ApiAdminActivityLogService);
  private readonly mapper = inject(MapperAdminActivityLogService);
  private readonly toast = inject(ToastService);

  items = signal<AdminActivityLog[]>([]);
  isLoading = signal(false);
  isDetailLoading = signal(false);
  isModalOpen = signal(false);
  selectedDetail = signal<AdminActivityLog | null>(null);
  searchText = signal('');
  actionFilter = signal('');
  entityTypeFilter = signal('');
  startDate = signal(this.formatDateInput(new Date()));
  endDate = signal(this.formatDateInput(new Date()));
  lastFetchedAt = signal<Date | null>(null);
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);

  readonly entityTypeOptions = [
    { value: '', label: 'ทุกประเภท' },
    { value: 'city_parking_register_member', label: 'สมัครสมาชิก' },
    { value: 'city_parking_register_member_group', label: 'กลุ่มลงทะเบียนจอดรถ' },
    { value: 'city_parking_vehicle', label: 'ทะเบียนรถ' },
    { value: 'parking_card', label: 'บัตรจอด' },
    { value: 'parking_transaction', label: 'รายการจอด' },
    { value: 'city_parking_invoice_header', label: 'ใบแจ้งหนี้' },
    { value: 'city_parking_invoice_details', label: 'รายการใบแจ้งหนี้' },
    { value: 'parking_finance', label: 'รายการรับชำระ' },
    { value: 'full_tax_invoice', label: 'ใบกำกับภาษีเต็มรูป' },
    { value: 'city_parking_topup_account', label: 'บัญชีเติมเงิน' },
    { value: 'city_parking_topup_transaction', label: 'รายการเติมเงิน' },
    { value: 'city_parking_customer', label: 'ลูกค้า' },
    { value: 'city_parking_address_invoice', label: 'ที่อยู่ใบแจ้งหนี้' },
    { value: 'city_parking_customer_lineoa', label: 'สมาชิก LINE ลูกค้า' },
    { value: 'city_parking_user', label: 'สมาชิก City Parking' },
    { value: 'city_parking_role', label: 'Role' },
    { value: 'city_parking_menu_role', label: 'Menu Role' },
    { value: 'res_users', label: 'ผู้ใช้ Backoffice' },
    { value: 'city_parking_user_session', label: 'Session ผู้ใช้' },
    { value: 'city_parking_approval_config', label: 'ตั้งค่าอนุมัติ' },
    { value: 'city_parking_approval_request', label: 'คำขออนุมัติ' },
    { value: 'city_parking_discount_free_range', label: 'ช่วงยกเว้นค่าจอด' },
    { value: 'city_parking_stamp_group', label: 'กลุ่ม Stamp' },
    { value: 'city_parking_stamp_amount_map', label: 'จับคู่ยอด Stamp' },
    { value: 'city_parking_stamp_billable_charge', label: 'รายการเรียกเก็บ Stamp' },
    { value: 'parking_site', label: 'ลานจอด' },
    { value: 'parking_client', label: 'ลูกค้าลาน (Client)' },
    { value: 'parking_vehicle_type', label: 'ประเภทรถ' },
    { value: 'parking_card_type', label: 'ประเภทบัตร' },
    { value: 'parking_card_mode', label: 'โหมดบัตร' },
    { value: 'parking_gate_type', label: 'ประเภทประตู' },
    { value: 'parking_gate', label: 'ประตู' },
    { value: 'parking_area', label: 'พื้นที่จอด' },
    { value: 'parking_gate_group', label: 'กลุ่มประตู' },
    { value: 'parking_rate', label: 'อัตราค่าบริการ' },
    { value: 'parking_main_rate', label: 'อัตราหลัก' },
    { value: 'parking_sub_rate', label: 'อัตราย่อย' },
    { value: 'parking_penalty_rate', label: 'อัตราปรับ' },
    { value: 'parking_subscription', label: 'แพ็กเกจสมาชิก' },
    { value: 'parking_fee', label: 'ค่าธรรมเนียม' },
    { value: 'parking_fee_group', label: 'กลุ่มค่าธรรมเนียม' },
    { value: 'city_parking_group_menu', label: 'กลุ่มเมนูหลัก' },
    { value: 'city_parking_group_sub_menu', label: 'กลุ่มเมนูย่อย' },
    { value: 'city_parking_menu_list', label: 'รายการเมนู' },
    { value: 'city_parking_news', label: 'ข่าว' },
    { value: 'city_parking_news_category', label: 'หมวดข่าว' },
    { value: 'city_parking_invoice_fee', label: 'ค่าธรรมเนียมใบแจ้งหนี้' },
    { value: 'city_parking_invoice_site_config', label: 'ตั้งค่าใบแจ้งหนี้ตามลาน' },
    { value: 'city_parking_issue_type', label: 'ประเภทเรื่อง CS' },
    { value: 'city_parking_service_case', label: 'เคส CS' },
    { value: 'prestamp_taiwan_request', label: 'Prestamp Taiwan' },
    { value: 'city_parking_ev_price_rate', label: 'อัตราค่าชาร์จ EV' },
    { value: 'parking_ev_connector_type', label: 'ประเภทหัวชาร์จ EV' },
    { value: 'city_parking_charging_jobs', label: 'งานชาร์จ EV' },
    { value: 'city_parking_transaction_ev', label: 'รายการชาร์จ EV' },
    { value: 'parking_finance_coupon', label: 'คูปอง' },
    { value: 'city_parking_discount', label: 'ส่วนลด City Parking' },
    { value: 'city_parking_stamp_list', label: 'Stamp (legacy)' },
    { value: 'city_parking_liff_home_menu_config', label: 'เมนู LIFF Home' },
    { value: 'city_parking_site_free_quota', label: 'โควต้าจอดฟรี (ลาน)' },
    { value: 'city_parking_client_free_quota', label: 'โควต้าจอดฟรี (Client)' },
    { value: 'city_parking_user_site_access', label: 'สิทธิ์ผู้ใช้ตามลาน' },
    { value: 'city_parking_access_group', label: 'กลุ่มสิทธิ์จอดฟรี' },
    { value: 'city_parking_line_guide_menu', label: 'คู่มือช่วยเหลือ LINE' },
    { value: 'city_parking_public_guide', label: 'คู่มือสาธารณะ (/manual)' },
    { value: 'city_parking_line_broadcast', label: 'ส่งข้อความ LINE OA (Broadcast)' },
    { value: 'city_parking_site_public_profile', label: 'ลานจอดที่แสดงหน้า Home' },
    { value: 'city_parking_bank_account', label: 'บัญชีธนาคาร' },
  ];

  readonly actionOptions = [
    { value: '', label: 'ทุกการกระทำ' },
    { value: 'create', label: 'สร้าง' },
    { value: 'update', label: 'แก้ไข' },
    { value: 'delete', label: 'ลบ' },
  ];

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(this.totalPages(), startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void {
    this.loadTable();
  }

  onSearchChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onDateChange(): void {
    if (!this.isDateRangeValid()) return;
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openDetail(item: AdminActivityLog): void {
    this.selectedDetail.set(null);
    this.isModalOpen.set(true);
    this.isDetailLoading.set(true);
    this.api.getAdminActivityLogById(item.id).subscribe({
      next: (res) => {
        this.selectedDetail.set(this.mapper.mapRow(res as AdminActivityLogDto));
        this.isDetailLoading.set(false);
      },
      error: () => {
        this.isDetailLoading.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedDetail.set(null);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_: number, item: AdminActivityLog): number {
    return item.id;
  }

  actionLabel(action: string): string {
    switch (action) {
      case 'create':
        return 'สร้าง';
      case 'update':
        return 'แก้ไข';
      case 'delete':
        return 'ลบ';
      default:
        return action || '—';
    }
  }

  /** Body field path → Thai form label. */
  fieldLabel(field: string): string {
    return adminActivityFieldLabel(field);
  }

  valueLabel(value: string | null | undefined): string {
    return adminActivityValueLabel(value);
  }

  entityLabel(entityType: string): string {
    const found = this.entityTypeOptions.find((o) => o.value === entityType);
    return found?.label && found.value ? found.label : entityType || '—';
  }

  loadTable(): void {
    if (!this.isDateRangeValid()) return;

    this.isLoading.set(true);
    this.api
      .getAdminActivityLogsForTable({
        page: this.currentPage(),
        pageSize: this.pageSize(),
        searchText: this.searchText().trim() || undefined,
        startDate: this.startDate(),
        endDate: this.endDate(),
        action: this.actionFilter() || undefined,
        entityType: this.entityTypeFilter() || undefined,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.data);
          this.totalItems.set(mapped.total);
          this.totalPages.set(Math.max(1, mapped.totalPages || Math.ceil(mapped.total / this.pageSize())));
          this.lastFetchedAt.set(new Date());
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดข้อมูลล้มเหลว');
        },
      });
  }

  private isDateRangeValid(): boolean {
    if (!this.startDate() || !this.endDate()) {
      this.toast.error('กรุณาระบุวันที่เริ่มต้นและวันที่สิ้นสุด');
      return false;
    }
    if (this.startDate() > this.endDate()) {
      this.toast.error('วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด');
      return false;
    }
    return true;
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
